import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const checkAuth = useCallback(async () => {
    console.log('[Admin Auth] Checking admin authentication...');
    try {
      const response = await axios.get(`${API}/admin/me`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      console.log('[Admin Auth] Admin authenticated:', response.data.email);
      setAdmin(response.data);
    } catch (error) {
      console.log('[Admin Auth] Not authenticated or session expired');
      setAdmin(null);
      setToken(null);
      localStorage.removeItem('admin_token');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    console.log('[Admin Auth] Login attempt:', email);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      const { token: newToken, admin: adminData } = response.data;
      console.log('[Admin Auth] Login successful:', adminData.email);
      setToken(newToken);
      localStorage.setItem('admin_token', newToken);
      setAdmin(adminData);
      return adminData;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Admin login failed';
      console.error('[Admin Auth] Login failed:', errorMessage, '- Email:', email);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
  };

  const logout = async () => {
    console.log('[Admin Auth] Logout initiated for:', admin?.email);
    try {
      await axios.post(`${API}/admin/logout`, {}, { 
        withCredentials: true,
        headers: getAuthHeaders()
      });
      console.log('[Admin Auth] Logout successful');
    } catch (error) {
      console.error('[Admin Auth] Logout error:', error.response?.data?.detail || error.message);
    }
    setAdmin(null);
    setToken(null);
    localStorage.removeItem('admin_token');
  };

  // API methods for admin panel
  const getDashboard = async () => {
    const response = await axios.get(`${API}/admin/dashboard`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getSettings = async () => {
    const response = await axios.get(`${API}/admin/settings`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const updateSettings = async (updates) => {
    const response = await axios.put(`${API}/admin/settings`, updates, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getIntegrations = async () => {
    const response = await axios.get(`${API}/admin/integrations`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const toggleIntegration = async (integration, enabled) => {
    const response = await axios.put(`${API}/admin/integrations/${integration}`, { enabled }, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getStripeStatus = async () => {
    const response = await axios.get(`${API}/admin/stripe/status`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const testStripeConnection = async () => {
    const response = await axios.post(`${API}/admin/stripe/test`, {}, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getWebhookLogs = async (limit = 20) => {
    const response = await axios.get(`${API}/admin/webhooks?limit=${limit}`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getAuditLogs = async (limit = 50) => {
    const response = await axios.get(`${API}/admin/audit-logs?limit=${limit}`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  // Subscription Admin Methods
  const getSubscriptionStats = async () => {
    const response = await axios.get(`${API}/admin/subscriptions/stats`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getSubscriptionTiers = async () => {
    const response = await axios.get(`${API}/admin/subscriptions/tiers`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const updateTierPrice = async (tier, price) => {
    const response = await axios.put(`${API}/admin/subscriptions/tiers/${tier}/price`, { price }, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const toggleTier = async (tier, enabled) => {
    const response = await axios.put(`${API}/admin/subscriptions/tiers/${tier}/toggle`, { enabled }, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const getOwnerSubscriptions = async (limit = 50) => {
    const response = await axios.get(`${API}/admin/subscriptions/owners?limit=${limit}`, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const changeUserSubscription = async (userId, tier) => {
    const response = await axios.put(`${API}/admin/subscriptions/user/${userId}/tier`, { tier }, {
      withCredentials: true,
      headers: getAuthHeaders()
    });
    return response.data;
  };

  const value = {
    admin,
    token,
    loading,
    isAuthenticated: !!admin,
    login,
    logout,
    checkAuth,
    getDashboard,
    getSettings,
    updateSettings,
    getIntegrations,
    toggleIntegration,
    getStripeStatus,
    testStripeConnection,
    getWebhookLogs,
    getAuditLogs,
    // Subscription admin methods
    getSubscriptionStats,
    getSubscriptionTiers,
    updateTierPrice,
    toggleTier,
    getOwnerSubscriptions,
    changeUserSubscription
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export default AdminContext;
